import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { ApiError } from '../api/client';
import type { Appointment, AppointmentCreate, FieldSalesCandidate, GeoStatus } from '../api/types';
import { GeoStatusBadge } from '../components/GeoStatusBadge';
import { MutationError } from '../components/MutationError';
import { SettingRow, SettingsRows } from '../components/Settings';
import { useDebounced } from '../components/useDebounced';
import { AddressAutocomplete } from '../places/AddressAutocomplete';
import { AddressCheck } from '../places/AddressCheck';
import { GoogleMapsAttribution } from '../places/GoogleAttribution';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { addDays, fmtDate, fmtTime, fromLocal, localDate, toLocalParts } from './format';

export interface AppointmentDefaults {
  startAt?: string;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string;
  // email: E-Mail des Eigentümers – Ziel der automatischen Terminbestätigung.
  email?: string | null;
}

// ViewingTarget: Besichtigung – der Lead ist das Objekt, der Interessent ein Kontakt (Bestätigung geht an ihn).
export interface ViewingTarget {
  contactId: string;
  exposeId?: string | null;
  name: string;
  email?: string | null;
  phone?: string | null;
}

interface Props {
  leadId: string;
  sourceCallId?: string;
  defaults?: AppointmentDefaults;
  viewing?: ViewingTarget;
  onSaved: (appointment: Appointment) => void;
  onCancel: () => void;
}

const geoMessages: Record<string, string> = {
  VALID: 'Adresse bestätigt',
  NOT_REQUIRED: 'Keine Adresse erforderlich',
};

// AppointmentForm: Terminmaske direkt im Arbeitsplatz. Adresse, Landkreis, Gebiet und passender
// Außendienst werden während der Eingabe geprüft (Micro-Geo-Check und Ranking im Backend).
export function AppointmentForm({ leadId, sourceCallId, defaults = {}, viewing, onSaved, onCancel }: Props) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const initial = defaults.startAt ? toLocalParts(defaults.startAt) : { date: addDays(localDate(), 1), time: '10:00' };
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [typeId, setTypeId] = useState('');
  const [duration, setDuration] = useState(0);
  const [form, setForm] = useState({
    contact_name: viewing?.name ?? defaults.contactName ?? '',
    contact_phone: viewing?.phone ?? defaults.contactPhone ?? '',
    street: defaults.street ?? '',
    house_number: defaults.houseNumber ?? '',
    postal_code: defaults.postalCode ?? '',
    city: defaults.city ?? '',
    notes: defaults.notes ?? '',
  });
  // Ohne eigene Auswahl gilt der automatische Vorschlag des Rankings.
  const [chosenFieldSales, setChosenFieldSales] = useState<string>();
  const [overrideReason, setOverrideReason] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [conflicts, setConflicts] = useState<string[]>([]);
  const [sendConfirmation, setSendConfirmation] = useState(true);
  const canOverride = can('appointment.override_assignment');

  const types = useQuery({
    queryKey: projectKeys.appointmentTypes(project.id),
    queryFn: ({ signal }) => papi.appointmentTypes(signal),
    staleTime: 5 * 60_000,
  });
  const activeTypes = (types.data?.items ?? []).filter((t) => t.is_active);
  const type = activeTypes.find((t) => t.id === typeId) ?? (viewing ? activeTypes.find((t) => t.key === 'VIEWING') : undefined) ?? activeTypes[0];
  const confirmationTo = viewing ? viewing.email : defaults.email;
  const minutes = duration || type?.default_duration_minutes || 60;
  const startAt = date && time ? fromLocal(date, time) : '';
  const addressKey = useDebounced(`${form.street}|${form.house_number}|${form.postal_code}|${form.city}`, 400);
  const [street, houseNumber, postalCode, city] = addressKey.split('|');

  const suggestion = useQuery({
    queryKey: ['projects', project.id, 'appointment-suggest', leadId, type?.id, startAt, minutes, addressKey, showAll],
    queryFn: ({ signal }) =>
      papi.suggestAppointment(
        {
          lead_id: leadId,
          type_id: type?.id,
          start_at: startAt,
          duration_minutes: minutes,
          street,
          house_number: houseNumber,
          postal_code: postalCode,
          city,
          include_out_of_area: showAll || undefined,
        },
        signal,
      ),
    enabled: !!startAt && !!type,
    placeholderData: (prev) => prev,
  });
  const candidates = suggestion.data?.candidates ?? [];
  const auto = suggestion.data?.auto_selected_user_id ?? '';
  const fieldSales = chosenFieldSales ?? auto;
  const selected = candidates.find((c) => c.user_id === fieldSales);
  const outOfArea = !!selected && !selected.in_area;

  const save = useMutation({
    mutationFn: (allowConflicts: boolean) => {
      const body: AppointmentCreate = {
        lead_id: leadId,
        type_id: type?.id,
        start_at: startAt,
        duration_minutes: minutes,
        contact_name: form.contact_name || null,
        contact_phone: form.contact_phone || null,
        street: form.street || null,
        house_number: form.house_number || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        notes: form.notes,
        source_call_id: sourceCallId,
        assigned_field_sales_user_id: fieldSales || undefined,
        override_reason: outOfArea ? overrideReason : undefined,
        allow_conflicts: allowConflicts || undefined,
        send_confirmation: sendConfirmation,
        ...(viewing ? { purpose: 'VIEWING' as const, contact_id: viewing.contactId, expose_id: viewing.exposeId ?? undefined } : {}),
      };
      return papi.createAppointment(body);
    },
    onSuccess: (appointment) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
      onSaved(appointment);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'appointment_conflict') setConflicts(error.fieldErrors.map((f) => f.message));
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setConflicts([]);
    save.mutate(false);
  };
  const geo = suggestion.data?.geo;
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });
  // Terminvorschläge: freie Zeiten mit nachvollziehbarem Score (Nähe zu bestehenden Terminen, Puffer, Auslastung).
  const [slotsFrom, setSlotsFrom] = useState('');
  const slots = useQuery({
    queryKey: ['projects', project.id, 'slots', leadId, type?.id, minutes, addressKey, slotsFrom],
    queryFn: ({ signal }) =>
      papi.recommendSlots(
        {
          lead_id: leadId,
          type_id: type?.id,
          duration_minutes: minutes,
          from: slotsFrom,
          days: 5,
          limit: 6,
          street,
          house_number: houseNumber,
          postal_code: postalCode,
          city,
        },
        signal,
      ),
    enabled: !!slotsFrom && !!type,
  });
  const pickSlot = (startIso: string, userId: string) => {
    const parts = toLocalParts(startIso);
    setDate(parts.date);
    setTime(parts.time);
    setChosenFieldSales(userId);
  };

  return (
    <form className="card appointment-form" onSubmit={submit} aria-label={viewing ? 'Besichtigung vereinbaren' : 'Termin vereinbaren'}>
      <div className="section-head">
        <h2>{viewing ? `Besichtigung mit ${viewing.name} vereinbaren` : 'Termin vereinbaren'}</h2>
        <button type="button" className="btn btn-small btn-ghost" onClick={() => setSlotsFrom(date && date >= localDate() ? date : localDate())}>
          Freie Zeiten vorschlagen
        </button>
      </div>
      {slotsFrom && (
        <div className="slot-suggestions" aria-live="polite">
          {slots.isPending ? (
            <p className="muted">Vorschläge werden berechnet …</p>
          ) : slots.isError ? (
            <MutationError error={slots.error} />
          ) : slots.data.slots.length === 0 ? (
            <p className="muted">{slots.data.reason || 'Keine passenden freien Zeiten gefunden.'}</p>
          ) : (
            <ul className="slot-list">
              {slots.data.slots.map((s) => (
                <li key={`${s.user_id}-${s.start_at}`}>
                  <button
                    type="button"
                    className="slot"
                    onClick={() => pickSlot(s.start_at, s.user_id)}
                    title={s.components.map((c) => `${c.label}: ${c.points > 0 ? '+' : ''}${c.points}`).join('\n')}
                  >
                    <strong>
                      {fmtDate(s.start_at)} {fmtTime(s.start_at)}
                    </strong>{' '}
                    · {s.user_name}
                    <span className="muted block">{s.explanation}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {slots.data?.attribution === 'GOOGLE_MAPS' && <GoogleMapsAttribution label="Fahrzeiten:" />}
        </div>
      )}
      <SettingsRows>
        <SettingRow label="Wann" hint="Datum, Uhrzeit und Dauer des Termins">
          <label className="field narrow">
            <span>Datum</span>
            <input type="date" value={date} min={localDate()} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="field narrow">
            <span>Uhrzeit</span>
            <input type="time" value={time} step={900} onChange={(e) => setTime(e.target.value)} required />
          </label>
          <label className="field narrow">
            <span>Dauer (Min.)</span>
            <input type="number" min={5} max={600} step={5} value={minutes} onChange={(e) => setDuration(Number(e.target.value))} />
          </label>
        </SettingRow>
        <SettingRow label="Terminart">
          <label className="field">
            <span className="visually-hidden">Terminart</span>
            <select
              value={type?.id ?? ''}
              onChange={(e) => {
                setTypeId(e.target.value);
                setDuration(0);
              }}
            >
              {activeTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          {type && !type.requires_address && !viewing && (
            <p className="muted small">Telefon- und Videotermine sind bei Maklern mit Ziel „Termin vor Ort“ nur die zweitbeste Stufe (80 %). Wenn möglich einen Termin vor Ort vereinbaren.</p>
          )}
        </SettingRow>
        <SettingRow label="Wer" hint="Ansprechpartner vor Ort">
          <label className="field">
            <span>Ansprechpartner</span>
            <input value={form.contact_name} onChange={set('contact_name')} />
          </label>
          <label className="field">
            <span>Telefon</span>
            <input value={form.contact_phone} onChange={set('contact_phone')} />
          </label>
        </SettingRow>
        <SettingRow label="Wo" hint="Adresse des Objekts – bestimmt Gebiet und Makler">
          <div className="appointment-address">
            <AddressAutocomplete
              label="Straße"
              value={form.street}
              onChange={(street) => setForm((f) => ({ ...f, street }))}
              context={[form.postal_code, form.city].filter(Boolean).join(' ')}
              onPick={(a) =>
                setForm((f) => ({ ...f, street: a.street || f.street, house_number: a.house_number || f.house_number, postal_code: a.postal_code, city: a.city }))
              }
            />
            <label className="field narrow">
              <span>Hausnr.</span>
              <input value={form.house_number} onChange={set('house_number')} />
            </label>
            <label className="field narrow">
              <span>PLZ</span>
              <input inputMode="numeric" maxLength={5} value={form.postal_code} onChange={set('postal_code')} />
            </label>
            <label className="field">
              <span>Ort</span>
              <input value={form.city} onChange={set('city')} />
            </label>
            <div className="field wide">
              <AddressCheck address={form} onApply={(a) => setForm((f) => ({ ...f, ...a }))} />
            </div>
          </div>
        </SettingRow>
      </SettingsRows>
      {geo && (
        <div className="geo-check" aria-live="polite">
          {geo.status === 'NOT_REQUIRED' ? <span className="badge badge-muted">Ohne Adresse</span> : <GeoStatusBadge status={geo.status as GeoStatus} />}
          <span>{geoMessages[geo.status] ?? geo.message}</span>
          {geo.county_name && <span className="muted">Landkreis {geo.county_name}</span>}
          <span className="muted">{geo.area_name ? `Gebiet ${geo.area_name}` : geo.area_ambiguous ? 'Gebiet mehrdeutig' : 'Kein Gebiet'}</span>
          {geo.address_differs_from_lead && (
            <span className="badge badge-warn" title={`Lead-Adresse: ${geo.lead_address}`}>
              Weicht von Lead-Adresse ab
            </span>
          )}
        </div>
      )}
      <fieldset className="field-sales">
        <legend>Außendienst</legend>
        {candidates.length === 0 ? (
          <p className="muted">{suggestion.data?.auto_selection_reason || 'Kein zuständiger Außendienst gefunden.'}</p>
        ) : (
          <ul className="candidate-list">
            {candidates.map((c) => (
              <CandidateOption
                key={c.user_id}
                candidate={c}
                checked={fieldSales === c.user_id}
                auto={auto === c.user_id}
                onChange={() => setChosenFieldSales(c.user_id)}
              />
            ))}
          </ul>
        )}
        {suggestion.data?.attribution === 'GOOGLE_MAPS' && <GoogleMapsAttribution label="Fahrzeiten:" />}
        {!auto && candidates.length > 1 && <p className="muted">{suggestion.data?.auto_selection_reason}</p>}
        {canOverride && (
          <label className="checkbox">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Auch Außendienst außerhalb des Gebiets zeigen
          </label>
        )}
        {outOfArea && (
          <label className="field wide">
            <span>Begründung der Ausnahme (Pflicht)</span>
            <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} required />
          </label>
        )}
      </fieldset>
      <label className="field wide">
        <span>Notizen</span>
        <textarea rows={2} value={form.notes} maxLength={4000} onChange={set('notes')} />
      </label>
      {confirmationTo ? (
        <label className="checkbox wide">
          <input type="checkbox" checked={sendConfirmation} onChange={(e) => setSendConfirmation(e.target.checked)} />{' '}
          {viewing ? 'Besichtigungsbestätigung' : 'Terminbestätigung'} mit Datenschutzinformation per E-Mail an {confirmationTo} senden
        </label>
      ) : (
        <p className="muted wide">
          {viewing ? 'Keine E-Mail-Adresse beim Interessenten' : 'Keine E-Mail-Adresse beim Eigentümer'} – die Bestätigung bitte telefonisch oder per Post.
        </p>
      )}
      {conflicts.length > 0 && (
        <div className="alert alert-warn" role="alert">
          <strong>Terminkonflikt</strong>
          <ul className="plain-list">
            {conflicts.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button type="button" className="btn" disabled={save.isPending} onClick={() => save.mutate(true)}>
            Trotzdem speichern
          </button>
        </div>
      )}
      {save.isError && !conflicts.length && <MutationError error={save.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={save.isPending || !type}>
          Termin speichern
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// CandidateOption: ein möglicher Außendienst mit Auslastung, Begründung und Konflikten (auch beim Neuzuweisen).
export function CandidateOption({
  candidate: c,
  checked,
  auto,
  disabled = false,
  note,
  onChange,
}: {
  candidate: FieldSalesCandidate;
  checked: boolean;
  auto: boolean;
  disabled?: boolean;
  note?: string;
  onChange: () => void;
}) {
  return (
    <li className={checked ? 'candidate selected' : 'candidate'}>
      <label>
        <input type="radio" name="field-sales" checked={checked} disabled={disabled} onChange={onChange} />
        <span className="candidate-name">
          {c.name}
          {auto && <span className="badge badge-ok">Vorschlag</span>}
          {!c.in_area && <span className="badge badge-warn">Außerhalb des Gebiets</span>}
          {note && <span className="badge badge-muted">{note}</span>}
        </span>
        <span className="muted candidate-meta">
          {c.appointments_today} Termine am Tag · {c.appointments_week} in der Woche
          {c.distance_km != null && ` · ${c.distance_km.toFixed(0)} km vom vorherigen Termin`}
        </span>
        {c.explanation && <span className="candidate-explanation">{c.explanation}</span>}
      </label>
      {c.conflicts.length > 0 && (
        <ul className="plain-list conflict-list">
          {c.conflicts.map((k) => (
            <li key={`${k.type}-${k.start_at}`} className={k.hard ? 'conflict hard' : 'conflict'}>
              {k.message}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
